import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/auth/AuthPages.css';

/**
 * ForgotPasswordPage - Request password reset email
 */
export const ForgotPasswordPage: React.FC = () => {
  const { forgotPassword, isLoggingIn, error, clearError } = useAuth();

  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [success, setSuccess] = useState(false);

  const validateForm = (): boolean => {
    if (!email.trim()) {
      setFieldError('L\'email est requis');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFieldError('Email invalide');
      return false;
    }
    setFieldError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSuccess(false);

    if (!validateForm()) return;

    try {
      const result = await forgotPassword(email);

      if (result.success) {
        setSuccess(true);
      }
    } catch (err) {
      // Error is already set by forgotPassword context function
      console.error('Forgot password error:', err);
    }
  };

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-box">
          <header className="auth-header">
            <h1 className="auth-logo">Corpus</h1>
            <h2 className="auth-title">Email envoyé</h2>
          </header>

          <div className="auth-alert success" role="alert">
            <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            <div className="auth-alert-content">
              <p className="auth-alert-title">Vérifiez votre boîte mail</p>
              <p className="auth-alert-message">
                Si un compte existe pour <strong>{email}</strong>, vous recevrez un email
                avec les instructions pour réinitialiser votre mot de passe.
              </p>
            </div>
          </div>

          <div className="auth-links">
            <Link to="/login" className="auth-link">
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-box">
        <header className="auth-header">
          <h1 className="auth-logo">Corpus</h1>
          <h2 className="auth-title">Mot de passe oublié ?</h2>
          <p className="auth-subtitle">
            Entrez votre email pour recevoir les instructions de réinitialisation.
          </p>
        </header>

        {error && (
          <div className="auth-alert error" role="alert">
            <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <div className="auth-alert-content">
              <p className="auth-alert-title">Erreur</p>
              <p className="auth-alert-message">{error}</p>
            </div>
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="auth-field">
            <label className="auth-label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className={`auth-input ${fieldError ? 'error' : ''}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.com"
              autoComplete="email"
              disabled={isLoggingIn}
            />
            {fieldError && (
              <p className="auth-field-error">{fieldError}</p>
            )}
          </div>

          <button type="submit" className="auth-submit" disabled={isLoggingIn}>
            {isLoggingIn ? (
              <>
                <span className="auth-spinner" />
                Envoi...
              </>
            ) : (
              'Envoyer les instructions'
            )}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/login" className="auth-link">
            Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
};
